import { graphql } from 'react-relay';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import type { NullableParentOption } from '@atlassian/jira-issue-field-parent-editview-full';
import type { AggJiraParentConnectionNode } from '@atlassian/jira-issue-field-parent-editview-full/src/ui/parent/types';
import { transformParentNode } from '@atlassian/jira-issue-field-parent-editview-full/src/utils/transform-data';
import type { parentIssueTransitionIssueAdjustmentsQuery as parentIssueTransitionIssueAdjustmentsQueryType } from '@atlassian/jira-relay/src/__generated__/parentIssueTransitionIssueAdjustmentsQuery.graphql';
import { FieldValueLookupError } from '../../../common/errors';
import type { ParentGetPublicValue, ParentSetPublicValue } from '../../../common/fields/parent';
import type { PublicToInternalPropertyTransformerContext } from '../../../common/types';
import type {
	FieldOptionsFilter,
	OptionsVisibility,
} from '../../../common/types/options-visibility';
import { fetchFieldOptions } from '../../../common/utils/fetch-field-options';
import { getResourceIdfromAri } from '../../../common/utils/get-resource-id-from-ari';

// eslint-disable-next-line @atlassian/relay/must-use-inline
export const parentIssueTransitionIssueAdjustmentsQuery = graphql`
	query parentIssueTransitionIssueAdjustmentsQuery(
		$ari: ID!
		$filterById: JiraFieldOptionIdsFilterInput
	) {
		node(id: $ari) {
			... on JiraParentIssueField {
				# eslint-disable-next-line @atlassian/relay/unused-fields
				id
				# eslint-disable-next-line @atlassian/relay/unused-fields
				parentCandidatesForExistingIssue(
					searchBy: ""
					filterById: $filterById
					excludeDone: false
				) {
					edges {
						node {
							id
							key
							issueColorField {
								color {
									colorKey
								}
							}
							fieldsById(ids: ["summary", "issuetype", "status"]) {
								edges {
									node {
										... on JiraSingleLineTextField {
											fieldId
											text
										}
										... on JiraIssueTypeField {
											fieldId
											issueType {
												issueTypeId
												name
												avatar {
													xsmall
												}
												hierarchy {
													level
												}
											}
										}
										... on JiraStatusField {
											fieldId
											status {
												statusCategory {
													statusCategoryId
												}
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

export const transformParentSetValue = async (
	inputValue: ParentSetPublicValue,
	_lookupValues: undefined,
	{
		tenantContext,
		viewContext,
		environment,
		fieldContext,
	}: PublicToInternalPropertyTransformerContext,
): Promise<NullableParentOption> => {
	if (!inputValue) return null;

	const optionAri = JiraIssueAri.create({
		siteId: tenantContext.cloudId,
		issueId: inputValue,
	}).toString();

	const response: {
		edges?: [
			{
				node?: AggJiraParentConnectionNode;
			},
		];
	} = await fetchFieldOptions<parentIssueTransitionIssueAdjustmentsQueryType>({
		tenantContext,
		viewContext,
		environment,
		fieldContext,
		optionARIs: [optionAri],
		nodePropertyName: 'parentCandidatesForExistingIssue',
		queryName: 'FETCH_PARENT',
		query: parentIssueTransitionIssueAdjustmentsQuery,
	});

	const parentValue = transformParentNode(response?.edges?.[0]?.node);
	if (parentValue?.value !== optionAri) {
		throw new FieldValueLookupError(inputValue);
	}

	return parentValue;
};

export const transformParentGetValue = (
	intervalValue: NullableParentOption,
): ParentGetPublicValue => {
	if (!intervalValue) {
		return null;
	}
	return {
		id: getResourceIdfromAri(intervalValue?.id?.toString() || ''),
		key: intervalValue.key,
	};
};

export const transformParentSetOptionsVisibility = (
	optionsVisibility: OptionsVisibility,
	_lookupValues: undefined,
	{ tenantContext }: PublicToInternalPropertyTransformerContext,
): FieldOptionsFilter => ({
	optionIds: optionsVisibility.options.map((optionId) =>
		JiraIssueAri.create({
			siteId: tenantContext.cloudId,
			issueId: optionId,
		}).toString(),
	),
	operation: optionsVisibility.isVisible ? 'ALLOW' : 'EXCLUDE',
});
